import { PointerEvent, useRef } from 'react'

import { ProfanityRuleItem, ProfanityRuleStatus } from '@/types/profanity'

import { PROFANITY_STATUS_CONFIG } from './profanityRuleUtils'

type ProfanityThresholdBarProps = {
  rules: ProfanityRuleItem[]
  disabled: boolean
  onThresholdChange: (status: ProfanityRuleStatus, value: number) => number
}

const statusStyles = Object.fromEntries(
  PROFANITY_STATUS_CONFIG.map((config) => [config.status, config])
) as Record<ProfanityRuleStatus, (typeof PROFANITY_STATUS_CONFIG)[number]>

const ProfanityThresholdBar = ({
  rules,
  disabled,
  onThresholdChange,
}: ProfanityThresholdBarProps) => {
  const barRef = useRef<HTMLDivElement>(null)
  const draggingStatus = useRef<ProfanityRuleStatus | null>(null)

  const getPointerPercentage = (clientX: number) => {
    const rect = barRef.current?.getBoundingClientRect()
    if (!rect?.width) return null
    return Math.round(((clientX - rect.left) / rect.width) * 100)
  }

  const updateBoundary = (status: ProfanityRuleStatus, percentage: number) => {
    const ruleIndex = rules.findIndex((rule) => rule.rule_status === status)
    if (ruleIndex < 0 || ruleIndex === rules.length - 1) return

    const rule = rules[ruleIndex]
    const nextRule = rules[ruleIndex + 1]
    const minimum = rule.min_threshold + 1
    const maximum = nextRule.max_threshold - 1
    onThresholdChange(status, Math.min(Math.max(percentage, minimum), maximum))
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || rules.length <= 1) return
    const percentage = getPointerPercentage(event.clientX)
    if (percentage === null) return

    const nearestBoundary = rules.slice(0, -1).reduce((nearest, rule) => (
      Math.abs(rule.max_threshold - percentage) < Math.abs(nearest.max_threshold - percentage)
        ? rule
        : nearest
    ))

    draggingStatus.current = nearestBoundary.rule_status
    event.currentTarget.setPointerCapture(event.pointerId)
    updateBoundary(nearestBoundary.rule_status, percentage)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingStatus.current) return
    const percentage = getPointerPercentage(event.clientX)
    if (percentage !== null) updateBoundary(draggingStatus.current, percentage)
  }

  const stopDragging = (event?: PointerEvent<HTMLDivElement>) => {
    draggingStatus.current = null
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  return (
    <div className="flex flex-col gap-sm">
      <div
        className={`relative mx-[6px] py-sm ${disabled || rules.length <= 1 ? 'cursor-default' : 'cursor-ew-resize'}`}
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={() => stopDragging()}
      >
        <div ref={barRef} className="flex h-[16px] w-full overflow-hidden rounded-full bg-gray-200 shadow-inner">
          {rules.map((rule) => (
            <div
              key={rule.rule_status}
              className={`h-full ${statusStyles[rule.rule_status].barClassName}`}
              style={{ width: `${rule.max_threshold - rule.min_threshold}%` }}
            />
          ))}
        </div>

        {rules.slice(0, -1).map((rule, index) => {
          const nextRule = rules[index + 1]
          const minimum = rule.min_threshold + 1
          const maximum = nextRule.max_threshold - 1
          const style = statusStyles[rule.rule_status]

          return (
            <button
              key={rule.rule_status}
              type="button"
              role="slider"
              aria-label={`${style.label} upper threshold`}
              aria-valuemin={minimum}
              aria-valuemax={maximum}
              aria-valuenow={rule.max_threshold}
              disabled={disabled}
              title={`${style.label}: ${rule.max_threshold}%`}
              className={`absolute top-1/2 h-[30px] w-[14px] -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full border-[3px] bg-white shadow-md outline-none transition-shadow focus:shadow-focus ${style.handleClassName}`}
              style={{ left: `${rule.max_threshold}%` }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
                  event.preventDefault()
                  updateBoundary(rule.rule_status, rule.max_threshold - 1)
                }
                if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
                  event.preventDefault()
                  updateBoundary(rule.rule_status, rule.max_threshold + 1)
                }
                if (event.key === 'Home') {
                  event.preventDefault()
                  updateBoundary(rule.rule_status, minimum)
                }
                if (event.key === 'End') {
                  event.preventDefault()
                  updateBoundary(rule.rule_status, maximum)
                }
              }}
            />
          )
        })}
      </div>

      <div className="flex justify-between text-text-sm text-gray-400">
        <span>0%</span>
        <span>100%</span>
      </div>
    </div>
  )
}

export default ProfanityThresholdBar
